import clsx from 'clsx';
import React from 'react';
import useFormFieldAria from '../../hooks/useFormFieldAria';

type ProgressBarBackground = 'default' | 'white' | 'grey';
type ProgressBarLabelAlign = 'left' | 'right';
type ProgressBarSize = 'default' | 'medium';
type ProgressBarVerticalPosition = 'top' | 'bottom';

type ProgressBarProps = {
  background?: ProgressBarBackground;
  hasPercentageLabel?: boolean;
  id?: string;
  isLabelHidden?: boolean;
  label: string;
  labelAlign?: ProgressBarLabelAlign;
  labelPosition?: ProgressBarVerticalPosition;
  percentagePosition?: ProgressBarVerticalPosition;
  progress?: number;
  size?: ProgressBarSize;
};

function ProgressBar({
  background = 'default',
  hasPercentageLabel = true,
  id,
  isLabelHidden = false,
  label,
  labelAlign = 'right',
  labelPosition = 'top',
  percentagePosition = 'bottom',
  progress = 0,
  size = 'default',
}: Readonly<ProgressBarProps>): JSX.Element {
  const { ids } = useFormFieldAria({ id });

  // Single sanitization point for ~20 consumers: guard against NaN/undefined
  // and clamp to 0–100 so aria-valuenow, the fill width and the visible % stay valid.
  const safeProgress = Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 100) : 0;

  return (
    <div
      aria-labelledby={ids.labelId}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={safeProgress}
      className={clsx(
        'progress-bar',
        `progress-bar--size-${size}`,
        `progress-bar--background-${background}`,
        `progress-bar--label-align-${labelAlign}`,
        `progress-bar--label-${labelPosition}`,
        `progress-bar--percentage-${percentagePosition}`
      )}
      role="progressbar"
    >
      {/* `label` is the required accessible name, linked via aria-labelledby.
          Use `isLabelHidden` to hide it visually (.sr-only) without losing it. */}
      <span className={clsx('progress-bar__text', { 'sr-only': isLabelHidden })} id={ids.labelId}>
        {label}
      </span>
      <div className="progress-bar__bar">
        <div aria-hidden="true" className="progress-bar__progress" style={{ width: `${safeProgress}%` }} />
      </div>
      {hasPercentageLabel && (
        <div aria-hidden="true" className="progress-bar__percentage">
          {safeProgress}%
        </div>
      )}
    </div>
  );
}

export default ProgressBar;
export type { ProgressBarBackground, ProgressBarLabelAlign, ProgressBarProps, ProgressBarSize, ProgressBarVerticalPosition };
